import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NbDialogRef, NbCardModule, NbInputModule, NbButtonModule } from '@nebular/theme';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { KPI } from '../../../@core/api';
import { I18nService, PriceSheetService, QuantityUnitsService, ScopeService, UsersService } from '../../../@core/utils';
import { AutoPpaService, PriceTier } from '../../../@core/utils/auto-ppa.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { idMapToArray } from '../../../@core/utils/scope.service';
import { ThousandsSeparatorPipe } from '../../pipes/thousands-separator.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UsernamePipe } from '../../pipes';
import { PriceSheetFormComponent } from '../side-forms/price-sheet-form/price-sheet-form.component';
import { AssetTypesService } from '../../../@core/utils/asset-types.service';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';
import { GROUP_PERMISSION_EDIT_PRICE_SHEET } from '../../../@core/api/models';

@Component({
    selector: 'app-price-sheet-details-dialog',
    styleUrls: ['./price-sheet-details-dialog.component.scss'],
    templateUrl: './price-sheet-details-dialog.component.html',
    standalone: true,
    imports: [
    NbCardModule,
    NbInputModule,
    NbButtonModule,
    AsyncPipe,
    FormsModule,
    TranslateModule,
    ThousandsSeparatorPipe,
    UsernamePipe,
    DatePipe
],
})
export class PriceSheetDetailsDialogComponent implements OnInit, OnDestroy {

  public priceSheetId: number;
  public assetTypeId: number;
  public currentAssetId: number;
  public priceSheet$: Observable<KPI.PriceSheet>;
  public assetType$: Observable<KPI.AssetType>;
  public currency: string = this.i18n.getCurrency();
  public lang: string = 'en';
  public quantityUnits: { [id: number]: string } = {}
  public seeDetails: boolean = false;
  public totalPrice: string;
  public selectedPriceTier: PriceTier = 'med';
  private subscriptions: Subscription[] = [];
  public canEdit: boolean;
  @ViewChild('commentDiv') private commentDivRef: ElementRef<HTMLDivElement>;
  public displayedComment: string = '';
  private rawComment: string = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private i18n: I18nService,
    private ref: NbDialogRef<PriceSheetDetailsDialogComponent>,
    private priceSheetService: PriceSheetService,
    private quantityUnitsService: QuantityUnitsService,
    private i18: I18nService,
    private scopeScervice: ScopeService,
    private rightPanelDialogService: RightPanelDialogService,
    private assetTypesService: AssetTypesService,
    private usersService: UsersService,
    private autoPpaService: AutoPpaService,
  ) { }

  public ngOnInit(): void {
    if (this.currentAssetId) {
      this.subscriptions.push(
        this.autoPpaService.autoPPA$.subscribe((autoPPA) => {
          this.selectedPriceTier = autoPPA[this.currentAssetId]?.selected_price_tier || 'med';
        })
      );
    }
    this.assetType$ = this.scopeScervice.assetTypes$.pipe(
      idMapToArray(),
      map((assetTypes) => assetTypes.find((at) => at.id === this.assetTypeId))
    );
    this.priceSheet$ = this.priceSheetService._pricesheetsHaveChanged$.pipe(
      switchMap(() => this.priceSheetService.getPriceSheet(this.priceSheetId)),
      tap((priceSheet) => {
        this.totalPrice = this.i18.formatCurrency(priceSheet.quantity * priceSheet.average_price);
        this.rawComment = priceSheet.comments || '';
        this.displayedComment = this.rawComment || '-';
        setTimeout(() => this.truncateComment());
      })
    );
    this.subscriptions.push(
      this.i18n.language$.subscribe((lang) => {
        if (lang) this.lang = lang;
      }),
      combineLatest([
        this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_PRICE_SHEET]),
        this.quantityUnitsService.quantityUnits$
      ])
      .subscribe(([canEdit, quantityUnits]) => {
        this.canEdit = canEdit;
        quantityUnits.forEach((quantityUnit) =>
          this.quantityUnits[quantityUnit.id] = quantityUnit.name)
      }),
      /*this.priceSheetService.priceSheetCostItems$.subscribe((priceSheetCostItems) => {
        priceSheetCostItems.forEach((costItem) =>
          this.priceSheetCostItems[costItem.id] = costItem.name
        );
      })*/
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private truncateComment(): void {
    if (!this.commentDivRef || !this.rawComment) return;
    const el = this.commentDivRef.nativeElement;
    if (el.scrollHeight <= el.clientHeight) return;
    const suffix = ' (...)';
    let lo = 0, hi = this.rawComment.length;
    while (hi - lo > 1) {
      const mid = Math.floor((lo + hi) / 2);
      el.textContent = this.rawComment.slice(0, mid) + suffix;
      if (el.scrollHeight <= el.clientHeight) lo = mid;
      else hi = mid;
    }
    this.displayedComment = this.rawComment.slice(0, lo) + suffix;
    this.cdr.detectChanges();
  }

  public close(): void {
    this.ref.close({ selectedPriceTier: this.selectedPriceTier });
  }

  public selectPriceTier(tier: PriceTier): void {
    this.selectedPriceTier = tier;
    if (this.currentAssetId) {
      const autoPPA = this.autoPpaService.autoPPA$.getValue();
      if (autoPPA[this.currentAssetId]) {
        autoPPA[this.currentAssetId].selected_price_tier = tier;
        this.autoPpaService.setAutoPPA(autoPPA, true);
      }
    }
  }

  public seeMore(): void {
    this.seeDetails = !this.seeDetails;
  }

  public deletePricesheet(pricesheet: KPI.PriceSheet): void {
    if(!pricesheet.is_default) {
      this.priceSheetService.deletePriceSheet(pricesheet.id).subscribe(() => {
        this.close();
        this.priceSheetService.pricesheetsHaveChanged();
      })
    } else {
      return
    }
  }

  public updatePriceSheet(priceSheet: KPI.PriceSheet) {
    this.subscriptions.push(
      this.assetTypesService.mappedAssetTypes$.subscribe((types) => {
        const assetType = types.find((type) => type.id === this.assetTypeId);
        this.rightPanelDialogService.open(
          PriceSheetFormComponent,
          {
            autoFocus: false,
            closeOnBackdropClick: false,
            context: {
              assetType: assetType,
              priceSheet: priceSheet,
              fromDetail: true
            },
          },
        )
      })
    )
  }

}
